import { useEffect, useState, type ReactNode } from 'react'
import { getPublicStats } from '../lib/api'

/**
 * DesktopShell — the persistent two-pane layout used on every
 * desktop surface (≥900px). Extracted from Home.tsx so Status can
 * wrap in it too, addressing the round-2 critic audit (E1: "Status
 * page is the single largest hierarchy regression on desktop —
 * apply the desktop shell to /status/:code, kill the 448px-on-1440px
 * column").
 *
 * Geometry:
 *   - Fixed full-screen, z-10, flex row
 *   - LEFT aside: 40% width (440px min, 600px max), warm cream
 *     paper, hairline-right border. Brand strip at top, page
 *     content as children below.
 *   - RIGHT slot: 60% width, page-specific content. On Home this
 *     is DesktopValueProp; on Status it's a quiet "what happens
 *     next" panel.
 */
export function DesktopShell({
  children,
  right,
}: {
  children: ReactNode
  right: ReactNode
}) {
  return (
    <div
      className="fixed inset-0 z-10 flex"
      style={{ background: 'var(--surface-0)' }}
    >
      <aside
        className="flex flex-col shrink-0 relative overflow-hidden"
        style={{
          width: '40%',
          minWidth: '440px',
          maxWidth: '600px',
          background: 'var(--surface-0)',
          borderRight: '1px solid var(--border-subtle)',
        }}
      >
        <BrandStrip />
        {/* Slot for the page's left-column content. flex-1 fills
            the remaining height under the brand strip. */}
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </aside>
      {right}
    </div>
  )
}

/**
 * BrandStrip — small, restrained, hairline-bottomed identity row
 * that anchors every desktop page. Gold tick → italic serif L →
 * caps "THE LOST CLUB" → caps trust line "FREE · NO SIGNUP · TWO
 * MINUTES". The trust line addresses P2's "no free / no signup
 * signal above the fold" critique; the persistent placement makes
 * the brand mark survive every state transition (per P1's "user
 * loses every anchor" critique on round 1).
 *
 * Type recipe is the canonical brand eyebrow (11px / 600 / 0.22em)
 * — round-3 unified the 5+ drift the round-1 audit caught.
 */
export function BrandStrip() {
  // Live "watching N items" counter — round-11 P2 trust-signal fix.
  // Fetches once on mount; if the number is small or fetch fails, the
  // line is suppressed entirely. Treats small numbers gracefully:
  // <10 = "Open beta", 10-49 = "Watching N items", 50+ = "Watching N
  // items right now" (P2: "if the number is real and ≥ 50, +0.5 to
  // Trust"). The single source of third-party-feeling proof on the
  // landing page until partnerships exist to name.
  const [activeCount, setActiveCount] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    getPublicStats().then((s) => {
      if (cancelled) return
      if (s && typeof s.active_items === 'number') setActiveCount(s.active_items)
    })
    return () => {
      cancelled = true
    }
  }, [])
  const counterCopy = (() => {
    if (activeCount === null) return null
    if (activeCount < 10) return 'Open · Watching every report'
    if (activeCount < 50) return `Watching ${activeCount} reports`
    return `Watching ${activeCount} reports right now`
  })()

  return (
    <div
      className="shrink-0 flex flex-col items-center justify-center"
      style={{
        paddingTop: 18,
        paddingBottom: 14,
        borderBottom: '1px solid var(--border-subtle)',
        gap: 2,
      }}
    >
      <div
        aria-hidden
        style={{ width: 18, height: 2, background: 'var(--gold)', marginBottom: 4 }}
      />
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '20px',
          lineHeight: 1,
          color: 'var(--ink-1)',
          fontStyle: 'italic',
          fontWeight: 400,
        }}
      >
        L
      </span>
      <span
        className="uppercase"
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--eyebrow-size)',
          fontWeight: 'var(--eyebrow-weight)',
          letterSpacing: 'var(--eyebrow-tracking)',
          color: 'var(--ink-1)',
          marginTop: 4,
        }}
      >
        The Lost Club
      </span>
      <span
        className="uppercase"
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--eyebrow-size-sm)',
          fontWeight: 'var(--eyebrow-weight)',
          letterSpacing: 'var(--eyebrow-tracking)',
          color: 'var(--ink-3)',
          marginTop: 6,
        }}
      >
        Free · No signup · Two minutes
      </span>
      {/* Live counter — fades in only when the API returns. Italic
          serif so it reads as program-note presence, not a marketing
          number. Per round-10 critic P2: "the cheapest version of a
          third-party signal is a live counter — if the number is
          real, it's the move that breaks the 8.7 trust ceiling." */}
      {counterCopy && (
        <span
          className="animate-fade-in"
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: '11px',
            color: 'var(--ink-3)',
            marginTop: 6,
          }}
        >
          {counterCopy}
        </span>
      )}
    </div>
  )
}
